import { useHoverSubmenu } from '@components/v2/ui/Menu/hooks/useHoverSubmenu';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import LangEnum from '../../../../../../lang/LangEnum';
import { useApplicationContext } from '../../../../../../modules/context/ApplicationContext/ApplicationContext';
import { Team } from '../../../../../../modules/context/ApplicationContext/applicationContext.types';
import {
  getBrandInfo,
  getStoreInfo,
} from '../../../../../../modules/context/ApplicationContext/applicationContext.utils';
import { useAuthContext } from '../../../../../../modules/login/AuthContext';
import { useFeatureVisibility } from '../../../../../../modules/permissions/contexts/FeatureVisibilityContext';
import { F } from '../../../../../../modules/permissions/types/features.keys';
import { useTranslation } from '../../../../../../modules/translation/TranslationContext';
import { User } from '../../../../../../modules/user-admin/user.types';
import {
  useFindAdminUserTeams,
  useFindTeams,
} from '../../../../../../services/modules/users/users.queries';
import { formatCompleteName } from '../../../../../../utils/formatters';
import PATHS from '../../../../../routes.const';
import { TeamSelectorGroup } from '../../components/TeamSelector/teamSelector.types';
import { UseUserMenuReturn } from './useUserMenu.types';
import { buildTeamGroups } from './useUserMenu.utils';

export const useUserMenu = (): UseUserMenuReturn => {
  const { state, changeTeam } = useApplicationContext();
  const { logout } = useAuthContext();

  const impersonatedUserId = state.user?.userId;
  const isImpersonating = Boolean(state.isAdminTeceo && impersonatedUserId);

  const ownTeamsQuery = useFindTeams({ enabled: !isImpersonating });
  const impersonatedTeamsQuery = useFindAdminUserTeams(impersonatedUserId, {
    enabled: isImpersonating,
  });
  const teamsQuery = isImpersonating ? impersonatedTeamsQuery : ownTeamsQuery;

  const history = useHistory();
  const { translate } = useTranslation();
  const { isVisible } = useFeatureVisibility();

  const [mainAnchorEl, setMainAnchorEl] = useState<HTMLElement | null>(null);
  const teamSubmenu = useHoverSubmenu();
  const [isUserSelectModalOpen, setIsUserSelectModalOpen] = useState(false);
  const [isAdminTeamModalOpen, setIsAdminTeamModalOpen] = useState(false);
  const [adminTeams, setAdminTeams] = useState<Team[]>([]);

  const allTeams = teamsQuery.data?.data ?? [];
  const currentTeam = state.user;
  const selectedTeamId = currentTeam?.id;

  const { name: brandName, logoUrl: brandLogoUrl } = getBrandInfo(state.user?.brand);
  const { name: storeName, logoUrl: storeLogoUrl } = getStoreInfo(state.user?.store);
  const entityName = brandName || storeName;
  const entityInitial = entityName ? entityName[0].toUpperCase() : '';
  const entityLogoUrl = brandLogoUrl || storeLogoUrl;
  const userName = formatCompleteName(state.user?.name, state.user?.surname, '');
  const userInitial = state.user?.name?.[0]?.toUpperCase() ?? '';
  const isAdminTeceo = isVisible(F.menu.adminTeceo);
  const canChangeTeam = isVisible(F.menu.teams);

  const groups: TeamSelectorGroup[] = buildTeamGroups({
    teams: allTeams,
    selectedTeamId,
    translate,
  });

  const closeAll = () => {
    setMainAnchorEl(null);
    teamSubmenu.close();
  };

  const handleCloseAdminTeamModal = () => {
    setIsAdminTeamModalOpen(false);
    setAdminTeams([]);
  };

  const handleSelectUser = (user: User) => {
    setAdminTeams(user.teams.map((team) => ({ ...team, name: user.name, surname: user.surname })));
    setIsUserSelectModalOpen(false);
    setIsAdminTeamModalOpen(true);
  };

  const handleBackToUserSelect = () => {
    setIsAdminTeamModalOpen(false);
    setAdminTeams([]);
    setIsUserSelectModalOpen(true);
  };

  return {
    buttonProps: {
      entityName,
      entityInitial,
      entityLogoUrl,
      isOpen: Boolean(mainAnchorEl),
      onClick: (e) => setMainAnchorEl(e.currentTarget),
    },
    mainMenuProps: {
      anchorEl: mainAnchorEl,
      onClose: closeAll,
      userName,
      userInitial,
      userLogoUrl: state.user?.profileUrl,
      isAdminTeceo,
      onAccountClick: () => {
        history.push(PATHS.settings.profile.path);
        closeAll();
      },
      onAdminClick: () => {
        history.push(PATHS.main.adminTeceo.path);
        closeAll();
      },
      onSignOutClick: () => {
        logout();
        closeAll();
      },
      onExternalLinkClick: (url) => {
        window.open(url, '_blank', 'noopener,noreferrer');
        closeAll();
      },
    },
    teamMenuProps: {
      ...teamSubmenu.submenuProps,
      groups,
      onSelect: (id) => {
        const team = allTeams.find((t) => t.id === id);
        if (team && team.id !== selectedTeamId) {
          changeTeam(team);
        }
        closeAll();
      },
    },
    teamItemProps: {
      ...teamSubmenu.triggerProps,
      show: canChangeTeam,
      isOpen: teamSubmenu.isOpen,
    },
    userItemProps: {
      show: isAdminTeceo,
      onClick: () => setIsUserSelectModalOpen(true),
    },
    userSelectModalProps: {
      open: isUserSelectModalOpen,
      onClose: () => setIsUserSelectModalOpen(false),
      onClick: handleSelectUser,
    },
    adminTeamModalProps: {
      open: isAdminTeamModalOpen,
      onClose: handleCloseAdminTeamModal,
      onBack: handleBackToUserSelect,
      teams: adminTeams,
      headerTextKey: LangEnum.TEAM_MODAL_CHOOSE_USER_TEAM_TEXT,
      onClick: (team) => {
        if (team.id !== selectedTeamId) {
          changeTeam(team);
        }
        handleCloseAdminTeamModal();
        closeAll();
      },
    },
  };
};
